import { useEffect, useState } from "react";

const SUPPORT_EMAIL = "support@koaform.com";
const MARKETING_URL =
  import.meta.env.VITE_MARKETING_URL ??
  (import.meta.env.PROD ? "https://koaform.com" : "http://localhost:5053");

/**
 * Getting help.
 *
 * A dialog rather than a link straight to the help page: the two things
 * someone clicking "?" actually wants are the address to write to and the
 * ability to copy it without retyping. Paid features route here too, which
 * is the whole contact-us model — so this address has to be impossible to
 * miss rather than buried on a marketing page.
 */
export const HelpDialog = ({ onClose }: { onClose: () => void }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Needs a secure context; the address is selectable regardless.
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Help and support"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="animate-fade fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-ink/45 p-6 backdrop-blur-[3px]"
    >
      <div className="animate-rise mt-[8vh] w-[460px] max-w-full rounded-[20px] bg-surface p-7 shadow-[0_30px_70px_-20px_rgba(12,26,46,0.5)]">
        <div className="mb-1.5 flex items-start justify-between gap-4">
          <h2 className="m-0 text-[21px] font-extrabold tracking-[-0.025em]">
            Need a hand?
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-[30px] w-[30px] flex-none rounded-lg bg-line text-[15px] text-muted hover:bg-line-input"
          >
            ×
          </button>
        </div>
        <p className="m-0 mb-6 text-[14.5px] leading-[1.55] text-muted">
          Email us and a person will answer. Tell us what you were doing and we
          will usually reply the same working day.
        </p>

        <div className="mb-2 flex gap-2">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="flex-1 rounded-[11px] bg-primary-600 px-5 py-3 text-center text-[15px] font-bold text-white hover:bg-primary-700 hover:text-white"
          >
            Email {SUPPORT_EMAIL}
          </a>
          <button
            type="button"
            onClick={() => void copy()}
            title="Copy the address"
            className="flex-none rounded-[11px] border border-line-bold bg-surface px-4 text-sm font-semibold text-ink hover:border-primary-600"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <p className="mt-4 mb-0 text-[13.5px] leading-[1.55] text-muted">
          Prefer to read first? The{" "}
          <a
            href={`${MARKETING_URL}/help`}
            target="_blank"
            rel="noreferrer noopener"
            className="font-semibold"
          >
            help pages
          </a>{" "}
          cover the common questions.
        </p>
      </div>
    </div>
  );
};
